import { EnrolledLiveClass } from './class';

declare global {
  interface Window {
    AwsWafCaptcha: any;
  }
}

// eslint-disable-next-line import/prefer-default-export
export enum Modality {
  Course = 'course',
  Lab = 'lab',
  VideoClassroom = 'video_classroom',
  Exam = 'exam',
  ExpertSeminar = 'expert_seminar',
  PremVTClass = 'prem_vt_class',
  PremVTEnrollment = 'prem_vt_enrollment',
  TechOverview = 'tech_overview',
}

/** Delivery-format extensions beyond catalog `Modality`. */
export enum ModalityExtension {
  CompReview = 'comp_review',
  EarlyAccess = 'early_access',
  ExamExperience = 'exam_experience',
  LabPlus = 'labPlus',
  Lesson = 'lesson',
  LiveEvents = 'liveEvents',
  LivePremiumSessionCourse = 'livePremiumSessionCourse',
  LiveVirtualClass = 'liveVirtualClass',
  PremiumSession = 'premiumSession',
  SkillsPath = 'skillsPath',
  SkillsAssessment = 'skillsAssessment',
}

export type OfferingTypes = Modality | ModalityExtension;

export enum CourseSubformat {
  CompReview = 'compreview',
}

export enum CourseFormat {
  Bundle = 'bundle',
}

export enum CourseType {
  Regular = 'regular',
  LabPlus = 'lab_plus',
  Lesson = 'module',
  HandsOnLab = 'hands_on_lab',
}

export enum ExamType {
  Regular = 'regular',
  Experience = 'experience',
}

export enum OfferingStatus {
  Active = 'active',
  Retired = 'retired',
  EarlyAccess = 'early',
}

/**
 * DeliveryFormat represents what the user sees in the UI
 */
export type DeliveryFormat =
  | Modality
  | CourseType
  | ExamType
  | CourseSubformat
  | OfferingStatus;

export enum OfferingTier {
  All = 'all',
  Base = 'base',
  Premium = 'premium',
  RHLSTrial = 'foundation',
}

export enum OfferingSuffix {
  EarlyAccess = 'ea',
}

export enum OfferingVisibility {
  Visible = 'visible', // user can see the offering
  Hidden = 'hidden', // user can't see the offering
  Upgrade = 'upgrade', // user can see the offering but it will be locked. Requires subscription upgrade.
}

export enum OfferingCodePrefix {
  EarlyAccess = 'ea',
}

export enum SubscriptionCode {
  FreeTier = 'ls000',
  NewFreeTier = 'ls001',
  LimitedTrial = 'ls999',
  Basic = 'ls120',
  Standard = 'ls220',
  Premium = 'ls520',
  RedHatUniversity = 'ls810',
  DeveloperTier = 'ls300',
  WorkForceDevelopmentTier = 'wdp200sp',
  SkillsBuild = 'xs001',
  IBMDigitalLearningSubscription = 'ls130c',
  RHLSTrial = 'ls020',
  RHLSCourse = 'rhls_course',

  // Retired:
  Promo = 'ls101',
  SubscriptionForPartners = 'ls105',
  NOTSURE = 'ls700',
}

/**
 * Namespaces also known as vocabulary slugs
 */
export enum VocabularyNamespaces {
  Modalities = 'modalities',
  Countries = 'countries',
  JobRoles = 'job_roles',
  Departments = 'departments',
  Languages = 'languages',
  InstructionLanguages = 'instruction_languages',
  CertificationPaths = 'certification_paths',
  SkillsPathTypes = 'skills_path_types',
  OfferingCategories = 'offering_categories',
  OfferingProducts = 'offering_products',
  OfferingTracks = 'offering_tracks',
  // related to new taxonomy:
  OfferingLevel = 'offering_level',
  OfferingTaxonomyAudiences = 'offering_taxonomy_audiences',
  OfferingTaxonomyTopics = 'offering_taxonomy_topics',
  OfferingTaxonomyProducts = 'offering_taxonomy_products',
}

export enum LegacyEnrollmentEtype {
  RHLSCourse = 'rhls_course',
  External = 'external',
  Open = 'open',
  // TODO: double check this
}

export interface VocabularyItem {
  token: string;
  display_name: string;
}

export type LegacyFilterTaxonomy = {
  /** @deprecated Use taxonomies or metadata field instead */
  audiences?: string[];
  /** @deprecated Use taxonomies or metadata field instead */
  products?: string[];
  /** @deprecated Use taxonomies or metadata field instead */
  categories?: string[];
  /** @deprecated Use taxonomies or metadata field instead */
  modalities?: string[];
};

export type NewFilterTaxonomy = {
  products?: string[]; // although similar to legacy's products, this field is used for new taxonomies
  audiences: string[];
  topics: string[];
};

export type CatalogEntry = {
  doc_type: 'catalog_entry';
  doc_id: string;
  slug: string;
  slug_tag?: string;
  status: OfferingStatus;
  sku?: string;
  code: string;
  language: string;
  format?: string;
  subformat?: CourseSubformat;
  '@timestamp': string;
  related_certifications: string[];
  visibility: OfferingVisibility;
  metadata?: CourseMetadata;
  modality: Modality;
  course_type?: CourseType;
  tier?: OfferingTier;
  collateral_id: string;
  version: string;
  epoch_number?: number;
  /**
   * Fallback title (often English). For user-facing UI, use
   * the utility function `getCourseTitleByLanguage` or `CatalogStore.getTitleWithFallback`
   * for translated titles.
   */
  title: string;
  bundle_reference?: string;
  source?: string;
  duration?: number;
  duration_unit?: string;
  total_duration?: number;
  total_ge?: number;
  total_labs?: number;
  job_roles?: string[];
  related_courses?: string[];
  translations?: [
    {
      language: string;
      title: string;
    },
  ];
  taxonomies?: NewFilterTaxonomy; // new taxonomies come in dedicated field
  level: number;
  exam_type?: ExamType;
  sort_date?: string;
  /**
   * Indicates if the entry has live classes available for enrollment. Only defined in offerings with modality "course".
   */
  has_lvc?: boolean;
} & LegacyFilterTaxonomy; // legacy taxonomies might still be present for older entries

/**
 * CatalogPolicies[slug][subscription]
 */
export type CatalogPolicies = Record<
  string,
  Partial<Record<SubscriptionCode, OfferingVisibility>>
>;

export interface Catalog {
  doc_type: string;
  '@timestamp': string;
  subscription_code: string;
  catalog_entries: CatalogEntry[];
}

export type LegacyCatalogSubscription = {
  username: string;
  slug_tag: string;
  end_time: string;
  level: string;
  etype: string;
  start_time: string;
  email: string;
  uuid: string;
  quotas?: { [key: string]: any };
  title: string;
};

export type LegacyEnrollment = {
  username: string;
  slug_tag: string;
  end_time: string; // This is an ISO 8601 date string
  level: string;
  etype: LegacyEnrollmentEtype;
  slug?: string;
  vtclass_product: string;
  start_time: string; // This is an ISO 8601 date string
  email: string;
  uuid: string; // This is a UUID string
  quotas?: { [key: string]: any };
  title: string;
};

/**
 * Nowadays mainly used to obtain Live classes
 */
export type LegacyCatalog = {
  /**
   * What is this new_entitlements?
   */
  new_entitlements: {
    role_vt: EnrolledLiveClass[];
    standard: LegacyEnrollment[];
    ilt: any[]; // TODO
    subscription: {
      username: string;
      slug_tag: string;
      end_time: string;
      level: string;
      etype: string;
      start_time: string;
      email: string;
      uuid: string;
      quotas?: { [key: string]: any };
      title: string;
    }[];
  };
  role_vt: EnrolledLiveClass[];
  upcoming_vt?: EnrolledLiveClass[];
  standard: LegacyEnrollment[];
  offerings: LegacyOffering[];
  ilt: EnrolledLiveClass[]; // Can we use LiveClass type or will it be different?
  subscription: LegacyEnrollment[];
};

export type LiveEvent = {
  id: string;
  slug?: string;
  title: string;
  startTime: string;
  endTime: string;
  type: 'premvt' | 'vt' | 'ilt' | 'lvc' | 'exam';
  /**
   * True is the event is ongoing
   */
  isCurrent: boolean;
  offeringId?: string;
  lmsOfferingSlug?: string;
};

export interface Collateral {
  associated_course_slugs?: string[];
  description?: { body: string };
  audience?: { body: string };
  prerequisites?: { body: string };
  lab_plus_courses?: { body: string };
}

export type LegacyOffering = {
  code: string;
  'course_header.code'?: string;
  'course_header.track'?: string;
  'course_header.version'?: string;
  full_slug?: string;
  id: string;
  lang_tag?: string;
  language: string;
  modality: string;
  slug: string;
  slug_tag: string;
  status: string;
  title: string;
  track: string;
  track_title?: string;
  updated: string;
  version: string;
  _catalog: boolean;
};

export type UserAchievement = {
  badge_image_url: string;
  title: string;
  issued_at: string;
  badge_url: string;
};

export enum CourseRelationType {
  ChildOf = 'child_of',
  ParentOf = 'parent_of',
  Precedes = 'precedes',
  Follows = 'follows',
  IsRelatedTo = 'is_related_to',
  RequiredBy = 'required_by',
}

export type CourseRelatedContent = {
  relation_type: CourseRelationType;
  code: string;
  version: string;
};

export type CourseMetadata = {
  doc_id: string;
  doc_type: string;
  '@timestamp': string;
  code: string;
  version: string;
  format: string;
  product: string;
  total_video_duration: number;
  skills: string[];
  certifications: string[];
  related_contents: CourseRelatedContent[];
  translations: string[];
  total_duration: number;
  total_labs: number;
  total_ge: number;
  total_quiz: number;
  description: Record<string, string>;
  objectives: Record<string, string[]>;
  audience?: Record<string, string[]>;
  prerequisites?: Record<string, string[]>;
  views: unknown[];
  duration_unit?: string; // any value from moment-duration-format, like 'minutes', 'hours', etc. Plural and singular are supported.
  taxonomies?: NewFilterTaxonomy; // new taxonomies come in dedicated field
  level: number;
  tier?: OfferingTier;
  subformat?: CourseSubformat;
} & LegacyFilterTaxonomy;

export type FeatureFlag = {
  active: boolean;
  name: string;
  super_roles?: string[];
  super_subscriptions?: string[];
  super_users?: string[];
};

export type FeatureSection = {
  section: string;
  flags: FeatureFlag[];
};

export type LearningPathElement = {
  user_progress?: number;
  code: string;
  title?: string;
  total_progress?: number;
};

export type LearningPath = {
  target_subscriptions: string[];
  doc_type: string;
  archived: boolean;
  code: string;
  title: string;
  joined?: boolean;
  joined_at?: string;
  lp_elements: LearningPathElement[];
  products: string[];
  user_progress?: number;
  path_type: string;
  categories: string[];
  levels?: number[];
  '@timestamp': string;
  /** Optional - present on standard paths, may be absent on custom */
  description?: {
    body: string;
    content_type: string;
  };
  /** Optional - present on standard paths, may be absent on custom */
  prerequisites?: {
    body: string;
    content_type: string;
  };
  language?: string;
  curriculum?: string;
  last_user_activity?: string;
  job_roles?: string[];
  author?: string;
  /** Optional - present on standard paths */
  translations?: {
    language: string;
    title: string;
  }[];
  taxonomies?: NewFilterTaxonomy;
  read_only?: boolean;
};

export type OrganizationManager = {
  state: string;
  reportees: number;
  id: string;
  username?: string;
  doc_type: 'user';
  domain?: string;
  uuid?: string;
  preferred_name?: string;
  '@timestamp'?: string;
  group_set?: string[];
};

export type OrganizationAdmin = {
  username: string;
  doc_type: 'user';
  domain: string;
  uuid: string;
  '@timestamp': string;
  group_set?: string[];
  preferred_name?: string;
  default_email?: string;
  emails?: string[];
};

export type Organization = {
  doc_type: 'organization';
  managers?: OrganizationManager[];
  name: string;
  admin_set?: OrganizationAdmin[];
  order_number_set?: string[];
  '@timestamp': string;
  manager_set?: OrganizationManager[];
  doc_id: string;
  id?: string;
  is_pending?: boolean;
};

export type UserEnrollmentInfo = {
  username: string;
  doc_type: 'user_enrollment_info';
  last_page_accessed: string;
  show_survey50: boolean;
  '@timestamp': string;
  _id: string;
  doc_id: string;
  offering_slug: string;
};

export enum TrialResponseCode {
  NotAuthenticated = 'E401',
  NotEnrolledToSku = 'E404',
  SubscriptionCreated = 'S200',
  ActiveSubscription = 'E403',
}

export type Snippet = {
  body: string;
  content_type?: string;
  language?: string;
};

export enum QuotaSlug {
  Exams = 'exams',
  ExamExperiences = 'experiences',
  LabHours = 'lab_hours',
  SessionsScheduled = 'sessions_scheduled',
  PremVTSessions = 'premvt_sessions',
  LVCEnrolledPerSKU = 'live_class_enrolled_per_sku',
  LVCCompletedPerSKU = 'live_class_completed_per_sku',
  LVCEnrolled = 'live_class_enrolled',
}
